// EventComments.js - Collapsible comments section for desktop event view
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import UserAvatar from '../../../UserAvatar';
import { createComment, getComments } from '../../../../services/postService';
import { useLanguage } from '../../../../contexts/LanguageContext';

/**
 * Desktop comments section for events - displays as a list when expanded
 */
const EventComments = ({ eventData, user, collapsed = true }) => {
    const { t } = useLanguage();
    const [comments, setComments] = useState([]);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingComments, setLoadingComments] = useState(true);
    const commentsEndRef = useRef(null);

    // Load comments when component mounts or event changes
    useEffect(() => {
        const fetchComments = async () => {
            if (!eventData?.id) return;

            setLoadingComments(true);
            try {
                const params = {
                    content_type: 'event',
                    content_id: eventData.id
                };

                const response = await getComments(params);
                setComments(response || []);
            } catch (error) {
                console.error('Error fetching comments:', error);
            } finally {
                setLoadingComments(false);
            }
        };

        fetchComments();
    }, [eventData?.id]);

    // Scroll to bottom when new comments are added
    useEffect(() => {
        if (!collapsed && commentsEndRef.current) {
            commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [comments.length, collapsed]);

    // Format timestamp for display
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp);
        const now = new Date();

        // Today: show time only
        if (date.toDateString() === now.toDateString()) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }

        // Yesterday
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) {
            return t('common.yesterday') + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }

        // Older: show date and time
        return date.toLocaleDateString([], { day: 'numeric', month: 'short' }) +
            ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Handle sending a new comment
    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!message.trim() || !eventData || !user) return;

        setLoading(true);

        try {
            const commentData = {
                content: message.trim(),
                content_type: 'event',
                content_id: eventData.id,
                community_id: eventData.community_id
            };

            // Create optimistic comment for immediate feedback
            const optimisticComment = {
                id: 'temp-' + Date.now(),
                content: message.trim(),
                content_type: 'event',
                content_id: eventData.id,
                user_id: user.id,
                email: user.email,
                display_name: user.displayName || user.display_name || user.email?.split('@')[0],
                profile_image: user.profileImage,
                created_at: new Date().toISOString(),
                community_id: eventData.community_id,
                isOptimistic: true
            };

            // Update local state immediately
            setComments(prev => [...prev, optimisticComment]);

            // Clear input field
            setMessage('');

            // Send to server
            const response = await createComment(commentData);

            // Replace optimistic comment with server response
            setComments(prev => prev.map(comment =>
                comment.id === optimisticComment.id ? {
                    ...response.comment,
                    email: user.email,
                    display_name: user.displayName || user.display_name || user.email?.split('@')[0],
                    profile_image: user.profileImage
                } : comment
            ));

        } catch (error) {
            console.error('Error posting comment:', error);
            // Remove optimistic comment on error
            setComments(prev => prev.filter(comment => !comment.isOptimistic));
            alert(t('events.comments.errorPosting') || 'Error posting comment. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Check if a comment is from the current user
    const isCurrentUserComment = (comment) => {
        return String(comment.user_id) === String(user?.id);
    };

    // Collapsed view: show comment count only
    if (collapsed) {
        return (
            <div className="text-muted small py-2">
                {loadingComments ? (
                    <span>
                        <FontAwesomeIcon icon={faCircleNotch} spin className="me-2" />
                        {t('common.loading')}
                    </span>
                ) : (
                    <span>
                        {comments.length === 0
                            ? t('events.comments.noComments') || 'No comments yet'
                            : t('events.comments.count', { count: comments.length }) || `${comments.length} comment${comments.length !== 1 ? 's' : ''}`
                        }
                    </span>
                )}
            </div>
        );
    }

    // Expanded view: show full comments list
    return (
        <div className="event-comments-section">
            {/* Comments list */}
            <div
                className="comments-list"
                style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    padding: '0.5rem 0'
                }}
            >
                {loadingComments ? (
                    <div className="text-center py-3">
                        <FontAwesomeIcon icon={faCircleNotch} spin className="me-2" />
                        {t('common.loading')}
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-muted text-center py-3">
                        {t('events.comments.noComments') || 'No comments yet. Be the first to comment!'}
                    </div>
                ) : (
                    comments.map((comment, index) => {
                        const isCurrentUser = isCurrentUserComment(comment);
                        const displayName = comment.display_name || comment.email?.split('@')[0] || 'User';

                        return (
                            <div
                                key={`comment-${comment.id || index}`}
                                className={`d-flex mb-2 ${isCurrentUser ? 'flex-row-reverse' : ''}`}
                                style={{ opacity: comment.isOptimistic ? 0.7 : 1 }}
                            >
                                {/* Avatar */}
                                <div className={isCurrentUser ? 'ms-2' : 'me-2'}>
                                    <UserAvatar
                                        profileImage={comment.profile_image}
                                        displayName={comment.display_name}
                                        email={comment.email}
                                        size={32}
                                        avatarOnly={true}
                                    />
                                </div>

                                {/* Comment bubble */}
                                <div
                                    style={{
                                        maxWidth: '70%',
                                        backgroundColor: isCurrentUser ? '#0d6efd' : '#f0f0f0',
                                        color: isCurrentUser ? 'white' : 'inherit',
                                        borderRadius: '12px',
                                        padding: '0.5rem 0.75rem'
                                    }}
                                >
                                    {/* Sender name (only for others) */}
                                    {!isCurrentUser && (
                                        <div className="small fw-bold mb-1" style={{ color: '#666' }}>
                                            {displayName}
                                        </div>
                                    )}

                                    {/* Comment text */}
                                    <div style={{ wordBreak: 'break-word' }}>
                                        {comment.content}
                                    </div>

                                    {/* Timestamp */}
                                    <div
                                        className="small"
                                        style={{
                                            opacity: 0.7,
                                            textAlign: isCurrentUser ? 'left' : 'right',
                                            marginTop: '0.25rem'
                                        }}
                                    >
                                        {formatTimestamp(comment.created_at)}
                                        {comment.isOptimistic && (
                                            <FontAwesomeIcon icon={faCircleNotch} spin className="ms-1" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={commentsEndRef} />
            </div>

            {/* Input area */}
            <form onSubmit={handleSendMessage} className="d-flex mt-2 gap-2">
                <input
                    type="text"
                    className="form-control"
                    placeholder={t('events.comments.placeholder') || 'Write a comment...'}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={loading}
                />
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!message.trim() || loading}
                >
                    <FontAwesomeIcon icon={loading ? faCircleNotch : faPaperPlane} spin={loading} />
                </button>
            </form>
        </div>
    );
};

export default EventComments;
